import { Helmet } from 'react-helmet-async';

export default function AccessibilityPage() {
  const features = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      title: 'Visual Accessibility',
      items: [
        'High contrast color schemes',
        'Scalable text and responsive layouts',
        'Alt text for all images',
        'Clear visual hierarchy'
      ]
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      ),
      title: 'Screen Reader Support',
      items: [
        'ARIA labels and landmarks',
        'Semantic HTML structure',
        'Descriptive link text',
        'Form field labels'
      ]
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
        </svg>
      ),
      title: 'Keyboard Navigation',
      items: [
        'Full keyboard accessibility',
        'Visible focus indicators',
        'Skip navigation links',
        'Logical tab order'
      ]
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Customization',
      items: [
        'Respects browser preferences',
        'Reduced motion support',
        'Adjustable text sizes',
        'Color scheme preferences'
      ]
    }
  ];

  return (
    <>
      <Helmet><title>Accessibility | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Accessibility Statement</h1>
            <p className="mt-4 text-gray-300">Our commitment to an inclusive experience</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            
            {/* Commitment */}
            <section className="mb-12">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-semibold text-navy mb-4">Our Commitment</h2>
                <p className="text-gray-600 mb-4">
                  BESTIX is committed to ensuring digital accessibility for people of all abilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards.
                </p>
                <p className="text-gray-600">
                  We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards, which define requirements for designers and developers to improve accessibility for people with disabilities.
                </p>
              </div>
            </section>

            {/* Features */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6 text-center">Accessibility Features</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center text-gold">
                        {feature.icon}
                      </div>
                      <h3 className="font-semibold text-navy text-lg">{feature.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {feature.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                          <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Standards */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Standards & Guidelines</h2>
              <div className="bg-white rounded-xl shadow-md p-6">
                <p className="text-gray-600 mb-4">
                  We aim to meet the following accessibility standards:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-navy rounded flex items-center justify-center text-white text-sm font-semibold">
                      AA
                    </div>
                    <div>
                      <h4 className="font-medium text-navy">WCAG 2.1 Level AA</h4>
                      <p className="text-sm text-gray-600">Web Content Accessibility Guidelines ensure content is accessible to a wide range of people with disabilities.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-navy rounded flex items-center justify-center text-white text-xs font-semibold">
                      ADA
                    </div>
                    <div>
                      <h4 className="font-medium text-navy">ADA Compliance</h4>
                      <p className="text-sm text-gray-600">We work to ensure our digital services meet Americans with Disabilities Act requirements.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-navy rounded flex items-center justify-center text-white text-xs font-semibold">
                      508
                    </div>
                    <div>
                      <h4 className="font-medium text-navy">Section 508</h4>
                      <p className="text-sm text-gray-600">We follow federal accessibility requirements for electronic and information technology.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Assistive Technology */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Assistive Technology Compatibility</h2>
              <div className="bg-white rounded-xl shadow-md p-6">
                <p className="text-gray-600 mb-4">
                  Our website is designed to be compatible with the following assistive technologies:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-gray-600">
                    <li>• JAWS (Windows)</li>
                    <li>• NVDA (Windows)</li>
                    <li>• VoiceOver (macOS, iOS)</li>
                  </ul>
                  <ul className="space-y-2 text-gray-600">
                    <li>• TalkBack (Android)</li>
                    <li>• Dragon NaturallySpeaking</li>
                    <li>• ZoomText</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Feedback */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Feedback & Assistance</h2>
              <div className="bg-white rounded-xl shadow-md p-6">
                <p className="text-gray-600 mb-4">
                  We welcome your feedback on the accessibility of BESTIX. Please let us know if you encounter any accessibility barriers:
                </p>
                <div className="space-y-3 text-gray-600">
                  <p><strong>Email:</strong> <span className="text-navy">accessibility@bestix.ai</span></p>
                  <p><strong>Phone:</strong> <span className="text-navy">1-888-BESTIX-1</span></p>
                </div>
                <p className="text-gray-600 mt-4">
                  We try to respond to accessibility feedback within 2 business days. If you need assistance with any part of our website, please contact us and we'll be happy to help.
                </p>
              </div>
            </section>

            {/* Ongoing Efforts */}
            <section>
              <div className="bg-navy rounded-xl p-8">
                <h3 className="text-xl font-semibold text-white mb-4">Ongoing Commitment</h3>
                <p className="text-gray-300 mb-4">
                  Accessibility is an ongoing effort. We continuously review our website against current accessibility standards and make improvements where needed. Our team receives regular training on accessibility best practices.
                </p>
                <p className="text-gray-300">
                  Last accessibility audit: January 2026
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </>
  );
}
